import React, { useState, useEffect } from 'react';
import { Leaf } from 'lucide-react';

interface LoaderProps {
  children: React.ReactNode;
}

const Loader: React.FC<LoaderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    // Check if this is the first visit
    const hasVisited = localStorage.getItem('angelCiteFirstVisit');
    
    if (hasVisited) {
      // User has visited before, skip loading animation
      setIsLoading(false);
      return;
    }

    // First visit - show loading animation
    const minimumLoadingTime = 5000; // Show for at least 2 seconds
    const startTime = Date.now();

    // Mark as visited immediately so next refreshes won't show
    localStorage.setItem('angelCiteFirstVisit', 'true');

    const timer = setTimeout(() => {
      setIsFadingOut(true);
      setTimeout(() => {
        setIsLoading(false);
      }, 800); // Wait for fade-out animation
    }, Math.max(0, minimumLoadingTime - (Date.now() - startTime)));

    return () => clearTimeout(timer);
  }, []);

  if (!isLoading) {
    return <>{children}</>;
  }

  return (
    <>
      {/* Loader Overlay */}
      <div 
        className={`fixed inset-0 z-[9999] flex items-center justify-center bg-white transition-opacity duration-800 ${
          isFadingOut ? 'opacity-0' : 'opacity-100'
        }`}
      >
        <div className="flex flex-col items-center space-y-6">
          {/* Animated Logo */}
          <div className="relative">
            {/* Outer spinning ring */}
            <div className="w-20 h-20 border-4 border-primary-200 rounded-full animate-spin" style={{
              animationDuration: '3s'
            }}></div>
            
            {/* Inner rotating ring */}
            <div className="absolute top-2 left-2 w-16 h-16 border-4 border-transparent border-t-primary-DEFAULT rounded-full animate-spin" style={{
              animationDuration: '1.5s',
              animationDirection: 'reverse'
            }}></div>
            
            {/* Center icon */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <Leaf className="w-8 h-8 text-primary-DEFAULT animate-pulse" />
            </div>
          </div>

          {/* Brand Name */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-800 font-poppins animate-pulse">
              Angel Cite
            </h1>
            <p className="text-gray-500 text-sm mt-2 animate-pulse">
              Fresh Agricultural Products
            </p>
          </div>

          {/* Loading dots */}
          <div className="flex space-x-2">
            <div className="w-3 h-3 bg-primary-DEFAULT rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-3 h-3 bg-primary-DEFAULT rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-3 h-3 bg-primary-DEFAULT rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>

          {/* Progress bar */}
          <div className="w-48 h-1 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-primary-DEFAULT rounded-full animate-loading-bar"></div>
          </div>
        </div>
      </div>

      {/* Main content underneath (fades in) */}
      <div className={`transition-opacity duration-800 ${
        isFadingOut ? 'opacity-100' : 'opacity-0'
      }`}>
        {children}
      </div>
    </>
  );
};

export default Loader;

